import React from "react";
import Modal from "../../../component/ui/modal";
import { useAppDispatch, useAppSelector } from "../../../store";
import { clear_lawyer, get_lawyer } from "../../../services/apis/lawyers";
import {
  clear_marketer,
  get_marketer,
} from "../../../services/apis/marketers";
import { IShowMarketer } from "../../../store/types/marketers";

type ProfessionalType = "marketer" | "lawyer";

interface ProfessionalViewModalProps {
  open: boolean;
  type: ProfessionalType;
  id: number | null;
  onClose: () => void;
}

interface DetailRowProps {
  label: string;
  value?: React.ReactNode;
}

const DetailRow = ({ label, value }: DetailRowProps) => (
  <div className="flex flex-col gap-1 border-b border-gray-100 py-3 sm:flex-row sm:gap-4">
    <dt className="w-44 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </dt>
    <dd className="min-w-0 break-words text-sm font-medium text-gray-800">
      {value ?? "—"}
    </dd>
  </div>
);

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const maskSensitiveValue = (value?: string) => {
  if (!value) return "—";
  return `••••${value.slice(-4)}`;
};

const ApplicationDetails = ({ application }: { application: IShowMarketer }) => (
  <section className="mt-6">
    <h3 className="mb-2 text-base font-semibold text-gray-900">
      Application details
    </h3>
    <dl className="divide-y divide-gray-100">
      <DetailRow label="Company" value={application.company_name} />
      <DetailRow label="Target country" value={application.target_country} />
      <DetailRow label="Payout currency" value={application.payout_currency} />
      <DetailRow
        label="Experience"
        value={
          application.experience_years != null
            ? `${application.experience_years} years`
            : "—"
        }
      />
      <DetailRow label="Sales background" value={application.sales_background} />
      <DetailRow
        label="Regions"
        value={application.regions?.length ? application.regions.join(", ") : "—"}
      />
      <DetailRow
        label="LinkedIn"
        value={
          application.linkedin_url ? (
            <a
              href={application.linkedin_url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              {application.linkedin_url}
            </a>
          ) : (
            "—"
          )
        }
      />
      <DetailRow label="Bank" value={application.bank_name} />
      <DetailRow label="Account name" value={application.account_name} />
      <DetailRow
        label="Account number"
        value={maskSensitiveValue(application.account_number)}
      />
      <DetailRow label="BVN" value={maskSensitiveValue(application.bvn)} />
      <DetailRow label="NIN" value={maskSensitiveValue(application.nin)} />
      <DetailRow label="Bank verification" value={application.bank_verified} />
      <DetailRow label="Application status" value={application.status} />
      <DetailRow label="Submitted" value={formatDate(application.created_at)} />
    </dl>
  </section>
);

const ProfessionalViewModal = ({
  open,
  type,
  id,
  onClose,
}: ProfessionalViewModalProps) => {
  const dispatch = useAppDispatch();
  const marketer = useAppSelector(({ marketer }) => marketer.marketer);
  const marketerLoading = useAppSelector(({ marketer }) => marketer.loading);
  const lawyer = useAppSelector(({ lawyers }) => lawyers.lawyer);
  const lawyerLoading = useAppSelector(({ lawyers }) => lawyers.loading);

  React.useEffect(() => {
    if (!open || id === null) return;

    const loadDetails = async () => {
      if (type === "marketer") {
        clear_marketer(dispatch);
        await get_marketer(dispatch, String(id));
      } else {
        clear_lawyer(dispatch);
        await get_lawyer(dispatch, String(id));
      }
    };

    void loadDetails();
  }, [dispatch, id, open, type]);

  const isLoading = type === "marketer" ? marketerLoading : lawyerLoading;
  const title = type === "marketer" ? "Marketer details" : "Lawyer details";
  const profile = type === "marketer" ? marketer : lawyer;

  return (
    <Modal
      title={title}
      open={open}
      onClose={onClose}
      onOkClick={onClose}
      hasButton={false}
      variant="medium"
    >
      {isLoading ? (
        <p className="py-8 text-center text-sm text-gray-500" role="status">
          Loading details...
        </p>
      ) : !profile ? (
        <p className="py-8 text-center text-sm text-gray-500">
          Details are unavailable.
        </p>
      ) : (
        <>
          <dl className="divide-y divide-gray-100">
            <DetailRow label="Name" value={profile.name} />
            <DetailRow label="Email" value={profile.email} />
            <DetailRow label="Username" value={profile.username} />
            <DetailRow label="Phone number" value={profile.phone_number} />
            <DetailRow
              label="Account status"
              value={profile.is_active ? "Active" : "Inactive"}
            />
            {"profile_status" in profile && (
              <DetailRow label="Profile status" value={profile.profile_status} />
            )}
            {"application_status" in profile && (
              <DetailRow
                label="Application status"
                value={profile.application_status}
              />
            )}
            {"referral_code" in profile && (
              <DetailRow label="Referral code" value={profile.referral_code} />
            )}
            <DetailRow label="Created" value={formatDate(profile.created_at)} />
            <DetailRow label="Updated" value={formatDate(profile.updated_at)} />
          </dl>
          {type === "marketer" && marketer?.application && (
            <ApplicationDetails application={marketer.application} />
          )}
        </>
      )}
    </Modal>
  );
};

export default ProfessionalViewModal;
